/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { spendRulesRulesUpdate } from "../funcs/spendRulesRulesUpdate.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { SpendRule } from "../models/components/spendrule.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  UpdateSpendRuleRequest,
  UpdateSpendRuleSecurity,
} from "../models/operations/updatespendrule.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type SpendRulesUpdateRuleMutationVariables = {
  request: UpdateSpendRuleRequest;
  security?: UpdateSpendRuleSecurity | undefined;
  options?: RequestOptions;
};

export type SpendRulesUpdateRuleMutationData = SpendRule;

export type SpendRulesUpdateRuleMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * updateSpendRule spendRules
 *
 * @remarks
 * Update a budget rule. Rule rows are immutable version snapshots: any change besides the enabled toggle archives the current version row and creates a successor at version + 1 (returned as the result, under a new ID). Enabled-only changes toggle the live row in place.
 */
export function useSpendRulesUpdateRuleMutation(
  options?: MutationHookOptions<
    SpendRulesUpdateRuleMutationData,
    SpendRulesUpdateRuleMutationError,
    SpendRulesUpdateRuleMutationVariables
  >,
): UseMutationResult<
  SpendRulesUpdateRuleMutationData,
  SpendRulesUpdateRuleMutationError,
  SpendRulesUpdateRuleMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildSpendRulesUpdateRuleMutation(client, options),
    ...options,
  });
}

export function mutationKeySpendRulesUpdateRule(): MutationKey {
  return ["@gram/client", "rules", "update"];
}

export function buildSpendRulesUpdateRuleMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: SpendRulesUpdateRuleMutationVariables,
  ) => Promise<SpendRulesUpdateRuleMutationData>;
} {
  return {
    mutationKey: mutationKeySpendRulesUpdateRule(),
    mutationFn: function spendRulesUpdateRuleMutationFn({
      request,
      security,
      options,
    }): Promise<SpendRulesUpdateRuleMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(spendRulesRulesUpdate(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
